/**
 * Fleet Trip Tracking — vehicle-wise and driver-wise trip reporting.
 * New for the RMC conversion. Reporting-only: reshapes Dispatch Challans
 * (each one IS a trip) and joins against Fuel Management logs for
 * diesel-per-trip / diesel-per-m3. Backend: routers/fleet.py.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Truck, UsersRound, ClipboardList } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet } from '../lib/api';
import { formatDate } from '../lib/format';

const TABS = ['Trips', 'Vehicle-wise', 'Driver-wise'];

export default function Fleet() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'Trips');
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TABS.includes(t) && t !== tab) setTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setTab = (t) => { setTabState(t); setSearchParams(t === 'Trips' ? {} : { tab: t }); };

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  return (
    <div data-testid="fleet-page">
      <PageHeader
        overline="Logistics"
        title="Fleet Trip Tracking"
        subtitle="Vehicle and driver-wise dispatch trips, with diesel consumption per trip and per m³ delivered."
      />
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex items-center justify-between overflow-x-auto">
          <div className="flex">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)}
                className={'relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ' + (tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]')}
                data-testid={`fleet-tab-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                {t}
                {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#1F7A6E]" />}
              </button>
            ))}
          </div>
          {tab !== 'Trips' && (
            <div className="flex items-center gap-2 pr-4 text-sm">
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-sm" data-testid="fleet-from-date" />
              <span className="text-slate-400">→</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-sm" data-testid="fleet-to-date" />
              {(fromDate || toDate) && (
                <button onClick={() => { setFromDate(''); setToDate(''); }} className="text-[#1F7A6E] hover:underline">Clear</button>
              )}
            </div>
          )}
        </div>
        <div className="p-5">
          {tab === 'Trips' && <TripsTab />}
          {tab === 'Vehicle-wise' && <VehicleSummaryTab fromDate={fromDate} toDate={toDate} />}
          {tab === 'Driver-wise' && <DriverSummaryTab fromDate={fromDate} toDate={toDate} />}
        </div>
      </div>
    </div>
  );
}

function KTile({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="kpi-overline">{label}</div>
        {Icon && <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${accent || 'bg-slate-100 text-slate-600'}`}><Icon size={13} /></div>}
      </div>
      <div className="font-display text-xl font-extrabold tracking-tight">{value}</div>
    </div>
  );
}

// ── Trips ────────────────────────────────────────────────────────────────────
function TripsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiGet('/fleet/trips'),
      apiGet('/fleet/dashboard'),
    ]).then(([trips, dash]) => { setItems(trips.items || []); setDashboard(dash); })
      .catch(() => toast.error('Failed to load trips'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-4">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
          <KTile label="Trips Today" value={dashboard.today_trip_count} icon={Truck} accent="text-[#1F7A6E] bg-teal-50" />
          <KTile label="Active Vehicles Today" value={dashboard.today_active_vehicles} icon={Truck} />
          <KTile label="Trips This Month" value={dashboard.month_trip_count} icon={ClipboardList} />
          <KTile label="Top Vehicle This Month"
            value={dashboard.top_vehicle_this_month ? `${dashboard.top_vehicle_this_month.vehicle_no} (${dashboard.top_vehicle_this_month.trip_count})` : '—'}
            icon={Truck} />
        </div>
      )}
      <div className="kpi-overline">Trips · {items.length}</div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Challan No</th><th>Date</th><th>Vehicle</th><th>Driver</th>
            <th>Party</th><th>Grade</th><th className="text-right">Qty (m³)</th><th>Status</th><th className="text-right">Turnaround (min)</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="9" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-slate-400">No trips yet — dispatch a challan to see it here.</td></tr>}
            {items.map((t) => (
              <tr key={t.challan_id}>
                <td className="font-mono text-xs text-[#1F7A6E] font-semibold">{t.challan_no}</td>
                <td className="text-xs">{formatDate(t.date)}</td>
                <td className="font-semibold">{t.vehicle_no}</td>
                <td className="text-xs">{t.driver_name || '—'}</td>
                <td className="text-xs">{t.party_name}</td>
                <td>{t.grade}</td>
                <td className="text-right tabular-nums">{t.qty_m3}</td>
                <td><span className={'tag ' + (t.status === 'cancelled' ? 'bg-red-50 text-red-700' : t.status === 'delivered' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700')}>{t.status}</span></td>
                <td className="text-right tabular-nums">{t.turnaround_minutes ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Vehicle-wise ─────────────────────────────────────────────────────────────
function VehicleSummaryTab({ fromDate, toDate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    apiGet(`/fleet/vehicle-summary${params.toString() ? `?${params}` : ''}`)
      .then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load vehicle summary'))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  return (
    <div>
      <div className="kpi-overline mb-3">Vehicles · {items.length}</div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Vehicle No</th><th className="text-right">Trips</th><th className="text-right">Qty Delivered (m³)</th>
            <th className="text-right">Fuel (L)</th><th className="text-right">Fuel Cost</th>
            <th className="text-right">Diesel / Trip (L)</th><th className="text-right">Diesel / m³ (L)</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-slate-400">No trips in this range.</td></tr>}
            {items.map((v) => (
              <tr key={v.vehicle_no}>
                <td className="font-semibold flex items-center gap-1.5"><Truck size={13} className="text-slate-400" /> {v.vehicle_no}</td>
                <td className="text-right tabular-nums font-display font-bold">{v.trip_count}</td>
                <td className="text-right tabular-nums">{v.qty_m3}</td>
                <td className="text-right tabular-nums">{v.fuel_l || '—'}</td>
                <td className="text-right tabular-nums">{v.fuel_cost ? `₹${v.fuel_cost.toLocaleString('en-IN')}` : '—'}</td>
                <td className="text-right tabular-nums">{v.diesel_per_trip_l ?? '—'}</td>
                <td className="text-right tabular-nums">{v.diesel_per_m3_l ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {items.length > 0 && items.every((v) => !v.fuel_l) && (
        <p className="text-xs text-slate-400 mt-2">No fuel logs found for these vehicles in this range — diesel figures need refuelling entries logged in Fuel Management with a matching vehicle number.</p>
      )}
    </div>
  );
}

// ── Driver-wise ──────────────────────────────────────────────────────────────
function DriverSummaryTab({ fromDate, toDate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    apiGet(`/fleet/driver-summary${params.toString() ? `?${params}` : ''}`)
      .then((r) => setItems(r.items || []))
      .catch(() => toast.error('Failed to load driver summary'))
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  return (
    <div>
      <div className="kpi-overline mb-3">Drivers · {items.length}</div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Driver</th><th className="text-right">Trips</th><th className="text-right">Qty Delivered (m³)</th>
            <th className="text-right">Fuel (L)</th><th className="text-right">Diesel / Trip (L)</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-slate-400">No trips in this range.</td></tr>}
            {items.map((d) => (
              <tr key={d.driver_name}>
                <td className="font-semibold flex items-center gap-1.5"><UsersRound size={13} className="text-slate-400" /> {d.driver_name}</td>
                <td className="text-right tabular-nums font-display font-bold">{d.trip_count}</td>
                <td className="text-right tabular-nums">{d.qty_m3}</td>
                <td className="text-right tabular-nums">{d.fuel_l || '—'}</td>
                <td className="text-right tabular-nums">{d.diesel_per_trip_l ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
